'use client';

import { useEffect, useRef, useCallback } from 'react';

/**
 * Runs a game tick at requestAnimationFrame cadence, in-process, without
 * re-rendering the component itself. The `step` callback is responsible for
 * setting state via its own closure.
 *
 * @param {{
 *   phase: 'running' | 'ended',
 *   tick: (nowMs: number) => void,
 * }} opts
 */
export function useGameLoop({ phase, tick }) {
  const rafRef = useRef(null);
  const tickRef = useRef(tick);
  tickRef.current = tick;

  useEffect(() => {
    if (phase !== 'running') return;
    const loop = (now) => {
      tickRef.current(now);
      rafRef.current = requestAnimationFrame(loop);
    };
    rafRef.current = requestAnimationFrame(loop);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [phase]);

  return useCallback(() => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
  }, []);
}